/**
 * Contractors — staff-managed subcontractor list (communication + selection
 * basics only; tax/1099/insurance/payment live in QuickBooks). Add, edit, and
 * activate/deactivate contractors used by the bid module. Staff-only.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getContractors,
  createContractor,
  updateContractor,
  getMe,
  type Contractor,
} from '../../lib/api';

const EMPTY = { name: '', email: '', phone: '', trade: '', notes: '' };

export function Contractors(): JSX.Element {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [list, setList] = useState<Contractor[]>([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMe()
      .then((me) => {
        const ok = me.role === 'ADMINISTRATOR' || me.role === 'ESTIMATOR';
        setAllowed(ok);
        if (ok) return getContractors().then(setList);
        return undefined;
      })
      .catch(() => setAllowed(false));
  }, []);

  async function refresh(): Promise<void> {
    setList(await getContractors());
  }

  async function add(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createContractor({
        name: form.name.trim(),
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(form.trade.trim() ? { trade: form.trade.trim() } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      });
      setForm({ ...EMPTY });
      await refresh();
    } catch {
      setError('Could not add the contractor. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(c: Contractor): Promise<void> {
    await updateContractor(c.id, { active: !c.active });
    await refresh();
  }

  if (allowed === null) {
    return <div className="flow flow--single"><div className="stage-card"><p className="stage-card__lede">Checking access&hellip;</p></div></div>;
  }
  if (!allowed) {
    return (
      <div className="flow flow--single"><div className="stage-card">
        <h1>Not available</h1>
        <p className="stage-card__lede">This area is for staff only.</p>
        <div className="q-actions"><button type="button" className="pb-btn" onClick={() => navigate('/dashboard')}>Back</button></div>
      </div></div>
    );
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Contractors</h1>
        <p className="stage-card__lede">
          Just the basics to reach them and pick one for a job. Tax, insurance, and payment live
          in QuickBooks.
        </p>

        {/* Add form */}
        <form onSubmit={add} className="pb-surface" style={{ marginBottom: 'var(--pb-3)' }}>
          <div className="pb-option__label" style={{ marginBottom: 'var(--pb-2)' }}>Add a contractor</div>
          <input className="pb-field" placeholder="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} style={{ marginBottom: 'var(--pb-2)' }} />
          <input className="pb-field" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} style={{ marginBottom: 'var(--pb-2)' }} />
          <input className="pb-field" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} style={{ marginBottom: 'var(--pb-2)' }} />
          <input className="pb-field" placeholder="Trade / specialty (e.g. Tile & bath)" value={form.trade} onChange={(e) => setForm((f) => ({ ...f, trade: e.target.value }))} style={{ marginBottom: 'var(--pb-2)' }} />
          <input className="pb-field" placeholder="Notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} style={{ marginBottom: 'var(--pb-2)' }} />
          {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}
          <button type="submit" className="pb-btn" disabled={busy || !form.name.trim()}>
            {busy ? 'Adding\u2026' : 'Add contractor'}
          </button>
        </form>

        {/* List */}
        {list.length === 0 ? (
          <p className="stage-card__lede">No contractors yet.</p>
        ) : (
          list.map((c) => (
            <div key={c.id} className="pb-surface" style={{ marginBottom: 'var(--pb-2)', opacity: c.active ? 1 : 0.55 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span className="pb-option__label">{c.name}{c.trade ? ` — ${c.trade}` : ''}</span>
                <button type="button" className="progress__back" onClick={() => void toggleActive(c)}>
                  {c.active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
              <div className="estimate__hint">
                {[c.email, c.phone].filter(Boolean).join(' \u00b7 ') || 'No contact details'}
                {c.active ? '' : ' \u00b7 inactive'}
              </div>
              {c.notes ? <div className="estimate__hint">{c.notes}</div> : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
